import { useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  announcementService,
  type AdminAnnouncement,
  type AnnouncementInput,
} from '../services/announcement.service';
import {
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_CATEGORY_DEFAULT_LABELS,
  type AnnouncementCategory,
} from '../constants/announcementCategories';

const EMPTY_FORM: AnnouncementInput = {
  category: 'general',
  titleJa: '',
  bodyJa: '',
  titleEn: '',
  bodyEn: '',
  linkUrl: '',
};

function toForm(row: AdminAnnouncement): AnnouncementInput {
  return {
    category: row.category,
    titleJa: row.titleJa,
    bodyJa: row.bodyJa,
    titleEn: row.titleEn ?? '',
    bodyEn: row.bodyEn ?? '',
    linkUrl: row.linkUrl ?? '',
  };
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ja-JP');
}

function errorMessage(error: unknown): string {
  const e = error as { response?: { data?: { error?: { message?: string }; message?: string } }; message?: string };
  return e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'error';
}

/** 研助お知らせの管理（作成・編集・公開・メール配信）。管理者のみ */
export default function AdminAnnouncements() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AdminAnnouncement | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<AnnouncementInput>(EMPTY_FORM);
  const [message, setMessage] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);
  const [sendTarget, setSendTarget] = useState<AdminAnnouncement | null>(null);

  const categoryLabel = (key: AnnouncementCategory) =>
    t(`announcements.category_${key}`, { defaultValue: ANNOUNCEMENT_CATEGORY_DEFAULT_LABELS[key] });

  const listQuery = useQuery({
    queryKey: ['announcements', 'admin'],
    queryFn: () => announcementService.listAdmin({ limit: 200 }),
    staleTime: 0,
  });

  const recipientsQuery = useQuery({
    queryKey: ['announcements', 'recipients', sendTarget?.id],
    queryFn: () => announcementService.recipients(sendTarget!.id),
    enabled: sendTarget != null,
    staleTime: 0,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['announcements'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (input: AnnouncementInput) => {
      const payload: AnnouncementInput = {
        ...input,
        titleEn: input.titleEn?.trim() ? input.titleEn : null,
        bodyEn: input.bodyEn?.trim() ? input.bodyEn : null,
        linkUrl: input.linkUrl?.trim() ? input.linkUrl : null,
      };
      return editing ? announcementService.update(editing.id, payload) : announcementService.create(payload);
    },
    onSuccess: () => {
      setMessage({ variant: 'success', text: t('announcements.admin_saved', { defaultValue: '保存しました。' }) });
      setEditing(null);
      setCreating(false);
      setForm(EMPTY_FORM);
      invalidate();
    },
    onError: (error) => setMessage({ variant: 'danger', text: errorMessage(error) }),
  });

  const publishMutation = useMutation({
    mutationFn: ({ id, published }: { id: number; published: boolean }) =>
      announcementService.setPublished(id, published),
    onSuccess: invalidate,
    onError: (error) => setMessage({ variant: 'danger', text: errorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => announcementService.delete(id),
    onSuccess: invalidate,
    onError: (error) => setMessage({ variant: 'danger', text: errorMessage(error) }),
  });

  const sendMutation = useMutation({
    mutationFn: (id: number) => announcementService.sendEmail(id),
    onSuccess: (result) => {
      setMessage({
        variant: 'success',
        text: t('announcements.admin_sent_result', {
          defaultValue: 'メール配信が終わりました。送信 {{sent}} 件 / 失敗 {{failed}} 件 / 対象 {{recipients}} 人',
          sent: result.sent,
          failed: result.failed,
          recipients: result.recipients,
        }),
      });
      setSendTarget(null);
      invalidate();
    },
    onError: (error) => setMessage({ variant: 'danger', text: errorMessage(error) }),
  });

  const startCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setCreating(true);
  };
  const startEdit = (row: AdminAnnouncement) => {
    setEditing(row);
    setForm(toForm(row));
    setCreating(true);
  };
  const cancelEdit = () => {
    setEditing(null);
    setCreating(false);
    setForm(EMPTY_FORM);
  };

  const rows = listQuery.data?.data ?? [];

  return (
    <Container className="py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h1 className="h3 mb-0">{t('announcements.admin_title', { defaultValue: 'お知らせ管理' })}</h1>
        <div className="d-flex gap-2">
          <Link to="/announcements" className="btn btn-outline-secondary btn-sm">
            {t('announcements.admin_open_public', { defaultValue: '公開ページを見る' })}
          </Link>
          <Button size="sm" onClick={startCreate} disabled={creating}>
            {t('announcements.admin_new', { defaultValue: '新しいお知らせ' })}
          </Button>
        </div>
      </div>
      <p className="text-muted small">
        {t('announcements.admin_lead', {
          defaultValue:
            '公開すると /announcements に載ります。メールは公開後に「メール配信」を押したときだけ、そのカテゴリを受信ONにしているユーザーへ送ります。日本語以外のユーザーには英語本文があれば英語で、無ければ日本語で送ります。',
        })}
      </p>

      {message ? (
        <Alert variant={message.variant} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      ) : null}

      {creating ? (
        <Card className="mb-4">
          <Card.Header>
            {editing
              ? t('announcements.admin_edit_heading', { defaultValue: 'お知らせを編集（#{{id}}）', id: editing.id })
              : t('announcements.admin_new', { defaultValue: '新しいお知らせ' })}
          </Card.Header>
          <Card.Body>
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                saveMutation.mutate(form);
              }}
            >
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="announcement-category">
                    <Form.Label>{t('announcements.admin_field_category', { defaultValue: '種類' })}</Form.Label>
                    <Form.Select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value as AnnouncementCategory })}
                    >
                      {ANNOUNCEMENT_CATEGORIES.map((key) => (
                        <option key={key} value={key}>
                          {categoryLabel(key)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group controlId="announcement-link">
                    <Form.Label>{t('announcements.admin_field_link', { defaultValue: 'リンク（任意・URL）' })}</Form.Label>
                    <Form.Control
                      type="url"
                      value={form.linkUrl ?? ''}
                      placeholder="https://kensuke-system.com/arena/fx"
                      onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="announcement-title-ja">
                    <Form.Label>{t('announcements.admin_field_title_ja', { defaultValue: '題名（日本語・必須）' })}</Form.Label>
                    <Form.Control
                      required
                      maxLength={200}
                      value={form.titleJa}
                      onChange={(e) => setForm({ ...form, titleJa: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="announcement-title-en">
                    <Form.Label>{t('announcements.admin_field_title_en', { defaultValue: '題名（英語・任意）' })}</Form.Label>
                    <Form.Control
                      maxLength={200}
                      value={form.titleEn ?? ''}
                      onChange={(e) => setForm({ ...form, titleEn: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="announcement-body-ja">
                    <Form.Label>{t('announcements.admin_field_body_ja', { defaultValue: '本文（日本語・必須）' })}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={8}
                      required
                      value={form.bodyJa}
                      onChange={(e) => setForm({ ...form, bodyJa: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="announcement-body-en">
                    <Form.Label>{t('announcements.admin_field_body_en', { defaultValue: '本文（英語・任意）' })}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={8}
                      value={form.bodyEn ?? ''}
                      onChange={(e) => setForm({ ...form, bodyEn: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex gap-2 mt-3">
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending
                    ? t('common.saving', { defaultValue: '保存中...' })
                    : t('announcements.admin_save', { defaultValue: '保存' })}
                </Button>
                <Button variant="outline-secondary" type="button" onClick={cancelEdit}>
                  {t('common.cancel', { defaultValue: 'キャンセル' })}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      ) : null}

      {listQuery.isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : null}
      {listQuery.isError ? <Alert variant="danger">{errorMessage(listQuery.error)}</Alert> : null}

      {rows.length > 0 ? (
        <Table responsive hover size="sm" className="align-middle">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('announcements.admin_col_category', { defaultValue: '種類' })}</th>
              <th>{t('announcements.admin_col_title', { defaultValue: '題名' })}</th>
              <th>{t('announcements.admin_col_status', { defaultValue: '状態' })}</th>
              <th>{t('announcements.admin_col_published_at', { defaultValue: '公開日時' })}</th>
              <th>{t('announcements.admin_col_mail', { defaultValue: 'メール' })}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{categoryLabel(row.category)}</td>
                <td>
                  <div>{row.titleJa}</div>
                  {row.titleEn ? <div className="small text-muted">{row.titleEn}</div> : null}
                </td>
                <td>
                  {row.isPublished ? (
                    <Badge bg="success">{t('announcements.admin_status_published', { defaultValue: '公開中' })}</Badge>
                  ) : (
                    <Badge bg="secondary">{t('announcements.admin_status_draft', { defaultValue: '下書き' })}</Badge>
                  )}
                </td>
                <td className="small">{formatDateTime(row.publishedAt)}</td>
                <td className="small">
                  {row.emailSentAt
                    ? t('announcements.admin_mail_sent', {
                        defaultValue: '{{count}} 件送信（{{at}}）',
                        count: row.emailSentCount,
                        at: formatDateTime(row.emailSentAt),
                      })
                    : t('announcements.admin_mail_not_sent', { defaultValue: '未配信' })}
                </td>
                <td>
                  <div className="d-flex flex-wrap gap-1 justify-content-end">
                    <Button size="sm" variant="outline-primary" onClick={() => startEdit(row)}>
                      {t('common.edit', { defaultValue: '編集' })}
                    </Button>
                    <Button
                      size="sm"
                      variant={row.isPublished ? 'outline-warning' : 'outline-success'}
                      disabled={publishMutation.isPending}
                      onClick={() => publishMutation.mutate({ id: row.id, published: !row.isPublished })}
                    >
                      {row.isPublished
                        ? t('announcements.admin_unpublish', { defaultValue: '非公開にする' })
                        : t('announcements.admin_publish', { defaultValue: '公開する' })}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-dark"
                      disabled={!row.isPublished}
                      onClick={() => setSendTarget(row)}
                    >
                      {t('announcements.admin_send_mail', { defaultValue: 'メール配信' })}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={deleteMutation.isPending}
                      onClick={() => {
                        if (window.confirm(t('announcements.admin_delete_confirm', { defaultValue: 'このお知らせを削除しますか？' }))) {
                          deleteMutation.mutate(row.id);
                        }
                      }}
                    >
                      {t('common.delete', { defaultValue: '削除' })}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {!listQuery.isLoading && rows.length === 0 ? (
        <p className="text-muted">{t('announcements.empty', { defaultValue: 'お知らせはまだありません。' })}</p>
      ) : null}

      <Modal show={sendTarget != null} onHide={() => setSendTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('announcements.admin_send_mail', { defaultValue: 'メール配信' })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sendTarget ? (
            <>
              <p className="fw-semibold mb-1">{sendTarget.titleJa}</p>
              <p className="small text-muted mb-3">{categoryLabel(sendTarget.category)}</p>
              {recipientsQuery.isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : recipientsQuery.data ? (
                <p className="mb-2">
                  {t('announcements.admin_recipients', {
                    defaultValue: '配信対象: {{total}} 人（日本語 {{ja}} / 英語 {{en}}）',
                    total: recipientsQuery.data.total,
                    ja: recipientsQuery.data.ja,
                    en: recipientsQuery.data.en,
                  })}
                </p>
              ) : null}
              {sendTarget.emailSentAt ? (
                <Alert variant="warning" className="small">
                  {t('announcements.admin_resend_warning', {
                    defaultValue: 'このお知らせは {{at}} に配信済みです。もう一度送ると、同じユーザーに再度届きます。',
                    at: formatDateTime(sendTarget.emailSentAt),
                  })}
                </Alert>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setSendTarget(null)}>
            {t('common.cancel', { defaultValue: 'キャンセル' })}
          </Button>
          <Button
            variant="primary"
            disabled={sendMutation.isPending || !recipientsQuery.data || recipientsQuery.data.total === 0}
            onClick={() => sendTarget && sendMutation.mutate(sendTarget.id)}
          >
            {sendMutation.isPending
              ? t('announcements.admin_sending', { defaultValue: '送信中...' })
              : t('announcements.admin_send_confirm', { defaultValue: '配信する' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
